import React, { useEffect, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import {
  applyVariantCarry,
  variantCarryRows,
  type VariantCarryChoice,
} from "./variantCarry";
import "./CreateVariantCarryModal.scss";

type Props = {
  product: any;
  onClose: () => void;
  onContinue: (product: any) => void;
};

const CreateVariantCarryModal = ({ product, onClose, onContinue }: Props) => {
  const rows = variantCarryRows(product);
  const [choices, setChoices] = useState<Record<string, VariantCarryChoice>>({});

  useEffect(() => {
    if (!product) {
      return;
    }
    const next: Record<string, VariantCarryChoice> = {};
    variantCarryRows(product).forEach((row) => {
      next[row.key] = {
        media: row.mediaCount > 0,
        taxonomy: row.hasTaxonomy,
      };
    });
    setChoices(next);
  }, [product]);

  const setChoice = (key: string, field: keyof VariantCarryChoice, checked: boolean) => {
    setChoices((current) => ({
      ...current,
      [key]: {
        media: current[key]?.media || false,
        taxonomy: current[key]?.taxonomy || false,
        [field]: checked,
      },
    }));
  };

  return (
    <Modal
      isOpen={Boolean(product)}
      toggle={onClose}
      fade={false}
      // Small phones: the list scrolls inside the modal so Cancel / Continue stay on screen.
      scrollable
      className="hkp-variant-carry"
    >
      <ModalHeader toggle={onClose}>Create variant</ModalHeader>
      <ModalBody>
        <p className="hkp-variant-carry__lead">
          Creating a variant of {product?.name || "this product"}. This piece gets its own
          studio SN, HKP tag number, and HKP ID.
        </p>
        <p className="hkp-variant-carry__hint">
          Process name, description, and artisan stay on the new piece. For each step,
          choose the media and taxonomy to keep.
        </p>
        {rows.length === 0 ? (
          <p className="hkp-variant-carry__empty">This product has no process steps yet.</p>
        ) : (
          <ul className="hkp-variant-carry__list">
            {rows.map((row) => (
              <li key={row.key} className="hkp-variant-carry__row">
                <span className="hkp-variant-carry__title">{row.title}</span>
                <div className="hkp-variant-carry__choices">
                <label className="hkp-variant-carry__option">
                  <input
                    type="checkbox"
                    checked={Boolean(choices[row.key]?.media)}
                    disabled={row.mediaCount === 0}
                    onChange={(event) => setChoice(row.key, "media", event.target.checked)}
                  />
                  <span>
                    Media{row.mediaCount > 0 ? ` (${row.mediaCount})` : ""}
                  </span>
                </label>
                <label className="hkp-variant-carry__option">
                  <input
                    type="checkbox"
                    checked={Boolean(choices[row.key]?.taxonomy)}
                    onChange={(event) => setChoice(row.key, "taxonomy", event.target.checked)}
                  />
                  <span>Taxonomy</span>
                </label>
                </div>
              </li>
            ))}
          </ul>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={() => onContinue(applyVariantCarry(product, choices))}
        >
          Continue
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CreateVariantCarryModal;
